/**
 * Komal OS - CRM: scan a business card, review the extraction, confirm.
 *
 * Stage 1 of the CRM module (see leadcapture-module-spec.md and
 * app/routes/crm_routes.py's module docstring) - the scanner only, no
 * Accounts/Deals list pages yet. A confirmed card creates (or matches
 * into) an Account + Contact.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Camera, UploadCloud, Loader2, CheckCircle2, AlertTriangle, RotateCcw, Clock, ListChecks, FileStack, PenLine,
} from 'lucide-react';
import { scanCard, getScanJob, confirmCard, listEvents, createEvent } from '../../services/crmService';
import useUIStore from '../../store/uiStore';

const FIELD_LABELS = {
  name: 'Name', title: 'Title', company: 'Company', phone: 'Phone',
  email: 'Email', address: 'Address', website: 'Website',
};
const FIELD_ORDER = ['name', 'title', 'company', 'phone', 'email', 'address', 'website'];

const blankFields = () => ({ name: '', title: '', company: '', phone: '', email: '', address: '', website: '' });

export default function CardScanPage() {
  const { showToast } = useUIStore();

  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState('');
  const [addingEvent, setAddingEvent] = useState(false);
  const [newEventName, setNewEventName] = useState('');

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [job, setJob] = useState(null); // current CardScanJob

  const [fields, setFields] = useState(blankFields());
  const [confidence, setConfidence] = useState({});
  // Any pen/pencil annotation the vision model spotted on the card (see
  // app/crm/vision_extract.py's handwritten_note field) - editable same
  // as the printed fields, logged to the account's Notes on confirm.
  const [handwrittenNote, setHandwrittenNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [dupDecision, setDupDecision] = useState(null); // {contact, account_name}

  const fileInputRef = useRef(null);
  const pollTimer = useRef(null);

  useEffect(() => {
    listEvents().then((res) => setEvents(res.data.events)).catch(() => {});
    return () => clearTimeout(pollTimer.current);
  }, []);

  const resetScan = () => {
    setFile(null);
    setPreviewUrl(null);
    setJob(null);
    setFields(blankFields());
    setConfidence({});
    setHandwrittenNote('');
    setDupDecision(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChosen = (chosen) => {
    if (!chosen) return;
    setFile(chosen);
    setPreviewUrl(URL.createObjectURL(chosen));
    setJob(null);
    setDupDecision(null);
  };

  const startScan = async () => {
    if (!file) return;
    setScanning(true);
    try {
      const res = await scanCard(file, eventId || undefined);
      setJob(res.data.job);
      if (res.data.job.status === 'done') applyResult(res.data.job);
      else if (res.data.job.status === 'failed') showToast(res.data.job.error || 'Card extraction failed', 'error');
      else pollJob(res.data.job.id); // queued or still processing
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not scan the card', 'error');
    } finally {
      setScanning(false);
    }
  };

  const pollJob = (jobId) => {
    pollTimer.current = setTimeout(async () => {
      try {
        const res = await getScanJob(jobId);
        setJob(res.data.job);
        if (res.data.job.status === 'done') applyResult(res.data.job);
        else if (res.data.job.status === 'failed') showToast(res.data.job.error || 'Card extraction failed', 'error');
        else pollJob(jobId); // still queued/processing
      } catch {
        pollJob(jobId); // transient poll failure - keep trying
      }
    }, 5000);
  };

  const applyResult = (doneJob) => {
    const r = doneJob.result || {};
    setFields({
      name: r.name || '', title: r.title || '', company: r.company || '',
      phone: r.phone || '', email: r.email || '', address: r.address || '', website: r.website || '',
    });
    setConfidence(r.confidence || {});
    setHandwrittenNote(r.handwritten_note || '');
  };

  const submitConfirm = async (decision) => {
    if (!fields.name.trim() || !fields.company.trim()) {
      showToast('Name and company are required', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        job_id: job.id,
        ...fields,
        handwritten_note: handwrittenNote.trim() || null,
        event_id: eventId || null,
        duplicate_decision: decision?.duplicate_decision,
        matched_contact_id: decision?.matched_contact_id,
      };
      const res = await confirmCard(payload);
      if (res.data.status === 'needs_decision') {
        setDupDecision(res.data.duplicate);
        return;
      }
      showToast(`Saved ${res.data.contact.name} at ${res.data.account.name}`, 'success');
      resetScan();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not save this contact', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateEvent = async () => {
    if (!newEventName.trim()) return;
    try {
      const res = await createEvent({ name: newEventName.trim() });
      setEvents((prev) => [res.data.event, ...prev]);
      setEventId(res.data.event.id);
      setNewEventName('');
      setAddingEvent(false);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not add event', 'error');
    }
  };

  const isBusy = job && ['pending', 'processing', 'queued'].includes(job.status);
  const isDone = job && job.status === 'done';

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-white">Scan a business card</h1>
            <p className="text-sm text-white/70 mt-0.5">Photo in, review the extraction, then save.</p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/crm/scan/multi"
              className="flex items-center gap-1.5 text-xs font-medium text-white/90 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3 py-2"
            >
              <FileStack size={14} /> Scan multiple cards
            </Link>
            <Link
              to="/crm/queue"
              className="flex items-center gap-1.5 text-xs font-medium text-white/90 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3 py-2"
            >
              <ListChecks size={14} /> Scan queue
            </Link>
          </div>
        </div>

        <div className="glass-card p-5 mb-5">
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Event (optional)</label>
          {!addingEvent ? (
            <div className="flex gap-2">
              <select
                value={eventId}
                onChange={(e) => setEventId(e.target.value)}
                className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm bg-white"
              >
                <option value="">— No event —</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>{ev.name}</option>
                ))}
              </select>
              <button
                onClick={() => setAddingEvent(true)}
                className="text-sm font-medium text-komal-blue px-3 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                + New
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                autoFocus
                value={newEventName}
                onChange={(e) => setNewEventName(e.target.value)}
                placeholder="e.g. IFAT India 2026"
                className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
              />
              <button onClick={handleCreateEvent} className="text-sm font-medium text-white bg-komal-blue px-3 py-2 rounded">Add</button>
              <button onClick={() => setAddingEvent(false)} className="text-sm text-gray-500 px-2">Cancel</button>
            </div>
          )}
        </div>

        {!job && (
          <div className="glass-card p-5 mb-5">
            {previewUrl ? (
              <div className="mb-4">
                <img src={previewUrl} alt="Card preview" className="w-full max-h-80 object-contain rounded-lg border border-gray-200" />
              </div>
            ) : (
              <label
                htmlFor="crm-card-upload"
                className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 hover:border-komal-blue rounded-xl py-14 cursor-pointer transition-colors"
              >
                <Camera size={30} className="text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">Take a photo or upload a business card</span>
                <span className="text-xs text-gray-400 mt-1">Works from your phone camera at an exhibition, or upload later</span>
              </label>
            )}
            <input
              id="crm-card-upload"
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFileChosen(e.target.files?.[0])}
            />

            <div className="flex gap-2 mt-4">
              {previewUrl && (
                <>
                  <label htmlFor="crm-card-upload" className="text-sm font-medium text-gray-600 border border-gray-300 rounded px-3 py-2 cursor-pointer hover:bg-gray-50 flex items-center gap-1.5">
                    <UploadCloud size={14} /> Choose a different photo
                  </label>
                  <button
                    onClick={startScan}
                    disabled={scanning}
                    className="flex-1 flex items-center justify-center gap-2 text-sm font-semibold text-white bg-komal-blue rounded px-3 py-2 disabled:opacity-60"
                  >
                    {scanning ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />}
                    {scanning ? 'Scanning…' : 'Scan card'}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {isBusy && (
          <div className="glass-card p-5 mb-5 flex items-center gap-3">
            <Loader2 size={18} className="animate-spin text-komal-blue shrink-0" />
            <div className="text-sm">
              {job.status === 'queued' ? (
                <>
                  <div className="font-semibold text-gray-800">
                    Provider is busy — queued{job.queue_position ? ` (position ${job.queue_position})` : ''}
                  </div>
                  <div className="text-gray-500">
                    <Clock size={12} className="inline -mt-0.5 mr-1" />
                    Next attempt in about {job.eta_seconds ? `${job.eta_seconds}s` : 'a minute'}. This page keeps checking — you can leave it open, or check the{' '}
                    <Link to="/crm/queue" className="text-komal-blue underline">scan queue</Link> later.
                  </div>
                </>
              ) : (
                <div className="font-semibold text-gray-800">Scanning card…</div>
              )}
            </div>
          </div>
        )}

        {isDone && !dupDecision && (
          <div className="glass-card p-5">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 size={16} className="text-green-600" />
              <span className="text-sm font-semibold text-gray-800">Review before saving</span>
            </div>

            <div className="grid gap-3">
              {FIELD_ORDER.map((f) => {
                const isLow = confidence[f] === 'low';
                return (
                  <div key={f}>
                    <label className="flex items-center gap-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      {FIELD_LABELS[f]}
                      {isLow && (
                        <span className="flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 text-[10px] font-bold normal-case tracking-normal">
                          <AlertTriangle size={10} /> check this
                        </span>
                      )}
                    </label>
                    <input
                      value={fields[f]}
                      onChange={(e) => setFields((prev) => ({ ...prev, [f]: e.target.value }))}
                      className={`w-full border rounded px-3 py-2 text-sm ${isLow ? 'border-amber-300 bg-amber-50' : 'border-gray-300'}`}
                    />
                  </div>
                );
              })}
            </div>

            <div className="mt-4">
              <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                <PenLine size={12} /> Handwritten note (if anything's written on the card)
              </label>
              <textarea
                value={handwrittenNote}
                onChange={(e) => setHandwrittenNote(e.target.value)}
                placeholder="e.g. Asked for a quote by Friday"
                rows={2}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
              {handwrittenNote.trim() && (
                <p className="text-[11px] text-gray-400 mt-1">Saved to this customer's Notes when you confirm.</p>
              )}
            </div>

            <div className="flex gap-2 mt-5">
              <button
                onClick={resetScan}
                className="flex items-center gap-1.5 text-sm font-medium text-gray-600 border border-gray-300 rounded px-3 py-2 hover:bg-gray-50"
              >
                <RotateCcw size={14} /> Discard
              </button>
              <button
                onClick={() => submitConfirm(null)}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 text-sm font-semibold text-white bg-green-700 rounded px-3 py-2 disabled:opacity-60"
              >
                {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                Confirm &amp; save
              </button>
            </div>
          </div>
        )}

        {dupDecision && (
          <div className="glass-card p-5">
            <div className="flex gap-3 items-start bg-amber-50 border border-amber-300 rounded-lg p-3 mb-4">
              <AlertTriangle size={16} className="text-amber-700 shrink-0 mt-0.5" />
              <div className="text-sm text-gray-800">
                <div className="font-semibold">Possible existing contact</div>
                <div className="mt-0.5">
                  <span className="font-medium">{dupDecision.contact.name}</span>
                  {dupDecision.account_name ? ` at ${dupDecision.account_name}` : ''} already matches by email or phone.
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => submitConfirm({ duplicate_decision: 'merge', matched_contact_id: dupDecision.contact.id })}
                disabled={saving}
                className="flex-1 text-sm font-semibold text-white bg-komal-blue rounded px-3 py-2 disabled:opacity-60"
              >
                Merge into existing
              </button>
              <button
                onClick={() => submitConfirm({ duplicate_decision: 'keep_separate', matched_contact_id: dupDecision.contact.id })}
                disabled={saving}
                className="flex-1 text-sm font-medium text-gray-700 border border-gray-300 rounded px-3 py-2 hover:bg-gray-50 disabled:opacity-60"
              >
                Keep separate
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
